import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Shield, Eye, Trash2, Loader2, KeyRound } from 'lucide-react';
import { formatDate, getInitials } from '../../utils/formatters';
import clsx from 'clsx';
import api from '../../api/client';

interface UserRecord {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  createdAt: string;
}

async function getUsers(): Promise<UserRecord[]> {
  const res = await api.get('/users');
  return res.data?.users || [];
}

async function createUser(data: { email: string; name: string; password: string; role: 'admin' | 'viewer' }): Promise<UserRecord> {
  const res = await api.post('/users', data);
  return res.data.user;
}

async function updateUserRole(id: string, role: string): Promise<void> {
  await api.put(`/users/${id}`, { role });
}

async function deleteUser(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

export default function TeamMembers() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'viewer' as 'admin' | 'viewer' });
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setShowAdd(false);
      setForm({ email: '', name: '', password: '', role: 'viewer' });
      setFormError('');
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || 'Failed to create user');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => updateUserRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Failed to delete user');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (form.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    createMutation.mutate(form);
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="card">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
          <button onClick={() => setShowAdd(true)} className="btn-primary gap-2">
            <UserPlus className="h-4 w-4" />
            Add User
          </button>
        </div>

        <div className="mt-4 divide-y divide-gray-200">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )}

          {!isLoading && members.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-400">No users found</p>
          )}

          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {getInitials(member.name || member.email)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{member.name || '—'}</p>
                  <p className="text-xs text-gray-500">{member.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Role badge / selector */}
                <select
                  value={member.role}
                  onChange={(e) => updateRoleMutation.mutate({ id: member.id, role: e.target.value })}
                  disabled={updateRoleMutation.isPending}
                  className={clsx(
                    'rounded-full border-0 px-2.5 py-1 text-xs font-medium focus:ring-2 focus:ring-brand-500',
                    member.role === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800',
                  )}
                >
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>

                <span className="text-xs text-gray-400">{formatDate(member.createdAt)}</span>

                <button
                  onClick={() => {
                    if (confirm(`Remove ${member.email}? This cannot be undone.`)) {
                      deleteMutation.mutate(member.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">Add User</h2>
            <p className="mt-1 text-sm text-gray-500">
              Create a user account directly. Share credentials with the user.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="input mt-1"
                  placeholder="Jane Smith"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="input mt-1"
                  placeholder="jane@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Default Password
                </label>
                <div className="relative mt-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className="input pr-10"
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    <KeyRound className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  The user should change this password after first login.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as 'admin' | 'viewer' }))}
                  className="input mt-1"
                >
                  <option value="viewer">Viewer — Can view data, cannot change settings</option>
                  <option value="admin">Admin — Full access to all settings</option>
                </select>
              </div>

              {formError && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</p>
              )}

              <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setFormError('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
